import BackendService from 'BackendService';
import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import type { FundUpdatesAdminState } from './fundUpdatesAdmin.types';

type SetAdminState = Dispatch<SetStateAction<FundUpdatesAdminState>>;

function useDeleteDraft(fundOpportunityId: string, setState: SetAdminState) {
  return useCallback(
    async (draft: AdminFundUpdateEntry) => {
      setState((prev) => ({ ...prev, discarding: true, error: null }));
      try {
        await new BackendService().discardFundUpdate(fundOpportunityId, draft.externalId);
        setState((prev) => ({
          ...prev,
          discarding: false,
          drafts: prev.drafts.filter((d) => d.externalId !== draft.externalId),
        }));
      } catch (err: unknown) {
        const error = err as {
          code?: string;
          issues?: Array<{ overridable?: boolean; issue?: string; message?: string }>;
        };
        if (
          error?.code === 'PROMOTION_PREREQUISITES' &&
          error?.issues?.some((i) => i.overridable)
        ) {
          const issue = error.issues.find((i) => i.issue === 'PREVIOUSLY_PUBLISHED');
          setState((prev) => ({
            ...prev,
            discarding: false,
            deleteConfirm: {
              update: draft,
              message:
                issue?.message || 'This update was previously published. Delete permanently?',
            },
          }));
          return;
        }
        console.error('[FundUpdatesAdminPanel] deleteDraft failed:', err);
        setState((prev) => ({
          ...prev,
          discarding: false,
          error: 'Unable to delete draft. Please try again.',
        }));
      }
    },
    [fundOpportunityId, setState],
  );
}

function useDeletePublished(fundOpportunityId: string, setState: SetAdminState) {
  return useCallback(
    async (update: AdminFundUpdateEntry) => {
      setState((prev) => ({ ...prev, error: null }));
      try {
        await new BackendService().discardFundUpdate(fundOpportunityId, update.externalId);
        setState((prev) => ({
          ...prev,
          published: prev.published.filter((p) => p.externalId !== update.externalId),
        }));
      } catch (err: unknown) {
        const error = err as {
          code?: string;
          error?: string;
          issues?: Array<{ issue?: string; message?: string }>;
        };
        if (error?.code === 'PROMOTION_PREREQUISITES' || error?.code === 'REQUIRES_CONFIRMATION') {
          const issue = error?.issues?.find((i) => i.issue === 'PREVIOUSLY_PUBLISHED');
          setState((prev) => ({
            ...prev,
            deleteConfirm: {
              update,
              message: issue?.message || error?.error || 'Delete this published update?',
            },
          }));
          return;
        }
        console.error('[FundUpdatesAdminPanel] deletePublished failed:', err);
        setState((prev) => ({
          ...prev,
          error: 'Unable to delete update. Please try again.',
        }));
      }
    },
    [fundOpportunityId, setState],
  );
}

function useConfirmDelete(
  fundOpportunityId: string,
  deleteConfirm: FundUpdatesAdminState['deleteConfirm'],
  setState: SetAdminState,
) {
  return useCallback(async () => {
    const confirmedUpdate = deleteConfirm?.update;
    if (!confirmedUpdate) return;

    setState((prev) => ({ ...prev, deleteInProgress: true }));

    try {
      await new BackendService().discardFundUpdate(fundOpportunityId, confirmedUpdate.externalId, {
        confirm: true,
      });
      setState((prev) => ({
        ...prev,
        drafts: prev.drafts.filter((d) => d.externalId !== confirmedUpdate.externalId),
        published: prev.published.filter((p) => p.externalId !== confirmedUpdate.externalId),
        deleteConfirm: null,
        deleteInProgress: false,
      }));
    } catch (err) {
      console.error('[FundUpdatesAdminPanel] confirmDelete failed:', err);
      setState((prev) => ({
        ...prev,
        deleteConfirm: null,
        deleteInProgress: false,
        error: 'Unable to delete update. Please try again.',
      }));
    }
  }, [fundOpportunityId, deleteConfirm, setState]);
}

export function useFundUpdateDeletion(
  fundOpportunityId: string,
  deleteConfirm: FundUpdatesAdminState['deleteConfirm'],
  setState: SetAdminState,
) {
  const handleDeleteDraft = useDeleteDraft(fundOpportunityId, setState);
  const handleDeletePublished = useDeletePublished(fundOpportunityId, setState);
  const handleConfirmDelete = useConfirmDelete(fundOpportunityId, deleteConfirm, setState);
  return { handleDeleteDraft, handleDeletePublished, handleConfirmDelete };
}
